import { computed } from 'vue'
import type { Ref } from 'vue'
import type {
  AbstractInventoryCalendarItem,
  DayNormalInventoryCalendarItem,
  DayNormalInventoryCalendarItemEvent,
  DayOnDemandInventoryCalendarItem,
  DayParcsInventoryCalendarItem,
  DaySummaryWithParcsInventoryCalendarItem,
  DaySummaryInventoryCalendarItem,
  MonthEventInventoryCalendarItem,
  MonthNormalInventoryCalendarItem,
  MonthOnDemandCalendarItem,
  MonthParcsInventoryCalendarItem,
} from '../calendarItemTypes'
import type { DayInventory, MonthInventory, LocationProductLookup } from '../types'
import type { GetInventoriesResponseInventory, GetInventoryWindowsResponseLp } from '../../../../../services/api-clients/InventoryApi'
import { Temporal } from '@js-temporal/polyfill'
import VerticalRegistry from '../../../../../verticals/VerticalRegistry'
import LocationProduct from '../../../../../models/LocationProduct'
import AbstractVertical from '../../../../../verticals/AbstractVertical'

export function useCalendarItems(
  dayInventory: DayInventory,
  monthInventory: MonthInventory,
  locationProducts: Ref<LocationProduct[]>,
  locationHasOnDemandLps: Ref<boolean>,
  locationHasParcsLps: Ref<boolean>,
) {

  const onDemandColor = VerticalRegistry.get('on_demand')?.color ?? '#787878'
  const parcsColor = VerticalRegistry.get('parcs')?.color ?? '#787878'
  const summaryColor = '#333333'

  const locationProductWeights = computed<{[key: number]: number}>(() => {
    const output: {[key: number]: number} = {}
    locationProducts.value.forEach((lp, i) => {
      output[lp.ID] = i
    })
    return output
  })

  const locationProductLookup = computed<LocationProductLookup>(() => {
    const output: LocationProductLookup = {}
    locationProducts.value.forEach(lp => {

      const vertical = VerticalRegistry.get(lp.product.verticalType)
      if (!vertical) {
        return
      }

      output[lp.ID] = {
        lp: lp,
        vertical: vertical,
      }

    })
    return output
  })

  const dayCalendarItems = computed<AbstractInventoryCalendarItem[]>(() => {

    const items: AbstractInventoryCalendarItem[] = []

    const nonParcsWindows: {[key: string]: any} = {}

    dayInventory.nonParcs.windows.forEach(win => {

      nonParcsWindows[win.start] = win

      // build summary item
      const item: DaySummaryInventoryCalendarItem = {
        type: 'day_summary',
        start: win.start,
        end: win.end,
        color: summaryColor,
        spacesSold: win.spacesSold,
        spacesTotal: win.spacesTotal,
      }

      items.push(item)

      const onDemandLpEntries: GetInventoryWindowsResponseLp[] = []

      // build normal items
      win.locationProducts.forEach(lpEntry => {

        const locationProduct = locationProductLookup.value[lpEntry.locationProductID] ?? null

        if (!locationProduct) {
          console.warn(`locationProduct ${lpEntry.locationProductID} not found`)
          return
        }

        if (locationProduct.lp.product.verticalType === 'on_demand') {
          onDemandLpEntries.push(lpEntry)
          return
        }

        const blackouts: {[key: number]: string} = {}

        lpEntry.blackoutIDs.forEach(blackoutId => {
          if (blackoutId in dayInventory.nonParcs.blackouts) {
            blackouts[blackoutId] = dayInventory.nonParcs.blackouts[blackoutId].name
          }
        })

        const events: {[key: number]: DayNormalInventoryCalendarItemEvent} = {}

        lpEntry.eventIDs.forEach(eventId => {
          if (eventId in dayInventory.nonParcs.events) {
            const event = dayInventory.nonParcs.events[eventId]
            events[eventId] = {
              venueId: event.venueID,
              venueName: event.venueName,
              eventId: event.ID,
              eventName: event.name,
              spacesSold: event.spacesSold,
            }
          }
        })

        const item: DayNormalInventoryCalendarItem = {
          type: 'day_normal',
          start: win.start,
          end: win.end,
          locationProduct: locationProduct.lp,
          color: locationProduct.vertical.color,
          spacesSold: lpEntry.spacesSold,
          spacesTotal: lpEntry.spacesTotal,
          spacesSoldPerChannel: lpEntry.spacesSoldPerChannel,
          blackouts: blackouts,
          events: events,
        }

        items.push(item)

      })

      // build On-Demand items
      if (locationHasOnDemandLps.value && onDemandLpEntries.length) {

        const spacesSold = onDemandLpEntries.reduce((subtotal, lpEntry) => subtotal + lpEntry.spacesSold, 0)

        const item: DayOnDemandInventoryCalendarItem = {
          type: 'day_on_demand',
          start: win.start,
          end: win.end,
          color: onDemandColor,
          spacesSold: spacesSold,
        }

        items.push(item)

      }

    })

    if (locationHasParcsLps.value) {

      // build PARCS items
      dayInventory.parcs.windows.forEach(win => {

        const parcsSold = win.completed + win.opened
        const nonParcsWindow = nonParcsWindows[win.start] ?? null
        const nonParcsSold = nonParcsWindow?.spacesSold ?? null
        const nonParcsTotal = nonParcsWindow?.spacesTotal ?? null

        const summaryItem: DaySummaryWithParcsInventoryCalendarItem = {
          type: 'day_summary_with_parcs',
          start: win.start,
          end: win.end,
          color: parcsColor,
          spacesSold: parcsSold + nonParcsSold,
          spacesTotal: nonParcsTotal,
        }

        items.push(summaryItem)

        const item: DayParcsInventoryCalendarItem = {
          type: 'day_parcs',
          start: win.start,
          end: win.end,
          color: parcsColor,
          completed: win.completed,
          opened: win.opened,
        }

        items.push(item)

      })

    }

    return items

  })

  const monthCalendarItems = computed<AbstractInventoryCalendarItem[]>(() => {

    const items: AbstractInventoryCalendarItem[] = []

    // group non-PARCS inventory by calendar item type

    interface InventoryWithMetadata {
      inventory: GetInventoriesResponseInventory
      locationProduct: LocationProduct
      vertical: AbstractVertical
    }

    const normalInventories: InventoryWithMetadata[] = []
    const eventInventories: InventoryWithMetadata[] = []
    const onDemandInventories: InventoryWithMetadata[] = []
    const unknownInventories: InventoryWithMetadata[] = []

    monthInventory.nonParcs.inventory.forEach(inventory => {

      const lp = locationProductLookup.value[inventory.locationProductID] ?? null

      if (!lp) {
        return
      }

      const iwm: InventoryWithMetadata = {
        inventory: inventory,
        locationProduct: lp.lp,
        vertical: lp.vertical,
      }

      const vertical = lp?.lp?.product?.verticalType ?? null

      if (['airport', 'transient', 'monthly', 'spothero_in_out'].includes(vertical)) {
        normalInventories.push(iwm)
      } else if (vertical === 'event') {
        eventInventories.push(iwm)
      } else if (vertical === 'on_demand') {
        onDemandInventories.push(iwm)
      } else {
        unknownInventories.push(iwm)
      }

    })

    if (unknownInventories.length) {
      console.warn('don\'t know how to render one or more inventory rows:', unknownInventories)
    }

    // build normal items
    normalInventories.forEach(iwm => {

      const dt = iwm.inventory.date.substring(0, 10)

      const item: MonthNormalInventoryCalendarItem = {
        type: 'month_normal',
        locationProduct: iwm.locationProduct,
        spacesSold: iwm.inventory.spacesSold,
        spacesTotal: iwm.inventory.spacesTotal,
        start: dt,
        end: dt,
        color: iwm.vertical?.color ?? '#787878',
      }

      items.push(item)

    })

    // build event items
    eventInventories.forEach(iwm => {

      if (!iwm.inventory.event) {
        console.warn('the API returned an inventory row for an event LP without the event data:', iwm.inventory)
        return
      }

      // The API returns datetimes with offset info - e.g., "2026-04-29T23:00:00-07:00". Remove it.
      const eventStart = iwm.inventory.event.start.substring(0, 19)
      const eventEnd = iwm.inventory.event.end.substring(0, 19)
      const parkingStart = iwm.inventory.event.parkingStart.substring(0, 19)
      const parkingEnd = iwm.inventory.event.parkingEnd.substring(0, 19)

      const item: MonthEventInventoryCalendarItem = {
        type: 'month_event',
        locationProduct: iwm.locationProduct,
        spacesSold: iwm.inventory.spacesSold,
        spacesTotal: iwm.inventory.spacesTotal,
        start: eventStart,
        end: eventStart, // intentional - we don't want the calendar item to span multiple days if the event runs past midnight
        color: 'transparent',
        // color: iwm.vertical?.color ?? '#787878',
        parkingStart: Temporal.PlainDateTime.from(parkingStart),
        parkingEnd: Temporal.PlainDateTime.from(parkingEnd),
        eventId: iwm.inventory.event.ID,
        eventName: iwm.inventory.event.name,
        eventStart: Temporal.PlainDateTime.from(eventStart),
        eventEnd: Temporal.PlainDateTime.from(eventEnd),
        venueId: iwm.inventory.event.venueID,
        venueName: iwm.inventory.event.venueName,
      }

      items.push(item)

    })

    // build on-demand items
    // all On-Demand inventories for a given date get displayed as a single calendar item
    const onDemandByDate = Object.groupBy(onDemandInventories, iwm => iwm.inventory.date.substring(0, 10))

    Object.entries(onDemandByDate).forEach(([isoDate, iwms]) => {

      const spacesSold = iwms!.reduce((subtotal, iwm) => subtotal + iwm.inventory.spacesSold, 0)

      const item: MonthOnDemandCalendarItem = {
        type: 'month_on_demand',
        start: isoDate,
        end: isoDate,
        color: onDemandColor,
        spacesSold: spacesSold
      }

      items.push(item)

    })

    // build PARCS items

    if (locationHasParcsLps.value) {

      Object.entries(monthInventory.parcs.dates).forEach(([isoDate, foo]) => {

        const item: MonthParcsInventoryCalendarItem = {
          type: 'month_parcs',
          start: isoDate,
          end: isoDate,
          color: parcsColor,
          opened: foo.opened,
          completed: foo.completed,
        }

        items.push(item)

      })

    }

    // sort
    items.sort((item1, item2) => {
      const item1Weight = getMonthItemWeight(item1)
      const item2Weight = getMonthItemWeight(item2)
      return item1Weight - item2Weight
    })

    return items

  })

  function getMonthItemWeight(item: AbstractInventoryCalendarItem) {
    if (item.type === 'month_parcs') {
      return 100
    } else if (item.type === 'month_on_demand') {
      return 110
    } else if (item.type === 'month_normal') {
      return locationProductWeights.value[item.locationProduct.ID] ?? 0
    } else if (item.type === 'month_event') {
      return 120 + (locationProductWeights.value[item.locationProduct.ID] ?? 0)
    }
    return 0
  }

  return {
    dayCalendarItems,
    monthCalendarItems,
  }

}
